<template>
  <div class="simple-input-group">
    <label :for="name">{{ t(`labels.${props.label ?? props.name}`) }}</label>
    <InputNumber
      :id="name"
      v-model="internalValue"
      class="w-full"
      :inputId="inputId"
      :minFractionDigits="minFractionDigits"
      :maxFractionDigits="maxFractionDigits"
      :mode="formatMode"
      :currency="currency"
      :locale="currentLocale"
      :disabled="disabled"
      :min="minValue"
      :max="maxValue"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  name: string;
  modelValue: number | undefined;
  initialValue: number | undefined;
  label?: string;
  disabled?: boolean;
  inputId?: string;
  formatType?: 'decimal' | 'currency'; // Optional format type (decimal or currency)
  currency?: string; // Optional currency symbol (e.g., EUR, USD)
  minValue?: number;
  maxValue?: number;
}>();
const { t, locale } = useI18n();

const internalValue = ref(props.modelValue);
const inputId = computed(() => props.inputId || 'locale-user');

// Set up format options based on props and locale
const formatMode = computed(() => props.formatType || 'decimal');
const currency = computed(() => props.currency || 'EUR');

// Use the current locale from i18n for number formatting
const currentLocale = computed(() => {
  switch (locale.value) {
    case 'de':
      return 'de-DE';
    case 'fr':
      return 'fr-FR';
    case 'hu':
      return 'hu-HU';
    case 'en':
    default:
      return 'en-GB'; // Default to UK English
  }
});

const minFractionDigits = computed(() => (formatMode.value === 'currency' ? 2 : 0));
const maxFractionDigits = computed(() => (formatMode.value === 'currency' ? 2 : 2));

const _emit = defineEmits<{
  (e: 'update:modelValue', value: number | undefined | null): void;
}>();

// Watch for changes in props.modelValue and update internalValue accordingly
watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue;
  },
);
</script>
